import { HoloVideoObject, HoloVideoFrontEnd, States, } from '@MixedRealityCaptureStudios/holo-video-object';
import * as Three from 'three';
export class HoloVideoObjectThreeJS extends HoloVideoFrontEnd {
    renderer;
    unlitMaterial;
    litMaterial;
    gl;
    clientBuffers;
    mesh;
    bufferGeometry;
    _createHoloVideoObject(context, createOptions, updateCurrentFrameCallback, errorCallback) {
        const hvo = new HoloVideoObject(context, createOptions, errorCallback);
        hvo.onUpdate = this.onUpdate.bind(this);
        return hvo;
    }
    constructor(renderer, callback, createOptions, updateCurrentFrameCallback, errorCallback) {
        super(renderer.getContext(), (fileInfo) => {
            this.onLoaded(fileInfo);
            callback(this.mesh);
        }, {
            outputLinearTextures: renderer.outputEncoding === Three.sRGBEncoding,
            ...createOptions,
        }, updateCurrentFrameCallback, errorCallback);
        this.renderer = renderer;
        const gl = renderer.getContext();
        this.gl = gl;
        this.clientBuffers = {
            tex: gl.createTexture(),
            indexBuf: gl.createBuffer(),
            norBuf: gl.createBuffer(),
            posBuf: gl.createBuffer(),
            uvBuf: gl.createBuffer(),
        };
        this.unlitMaterial = new Three.MeshBasicMaterial({ map: null, transparent: false, side: Three.DoubleSide });
        this.litMaterial = new Three.MeshPhysicalMaterial({
            map: null, transparent: false, side: Three.FrontSide, roughness: 1, metalness: 0,
        });
    }
    release() {
        this.unlitMaterial.dispose();
        this.litMaterial.dispose();
    }
    onLoaded(fileInfo) {
        this._fileInfo = fileInfo;
        const useNormals = fileInfo.haveNormals;
        const indexType = fileInfo.indicesComponentType;
        if (this.hvo.getGraphicsBackend().contextRestoring) {
            if (this.mesh) {
                this.mesh.material.map = this._setVertexAttributes(this.mesh.geometry);
                this.mesh.material.needsUpdate = true;
            }
            return;
        }
        if (this.mesh) {
            const indexAt = this.mesh.geometry.getIndex();
            const normalAt = this.mesh.geometry.getAttribute('normal');
            const haveNormals = normalAt != undefined;
            if (indexAt.type != indexType || haveNormals != useNormals) {
                this.mesh = null;
            }
            else {
                const material = useNormals ? this.litMaterial : this.unlitMaterial;
                material.map = this.mesh.material.map;
                this.mesh.material = material;
            }
        }
        if (!this.mesh) {
            const bufferGeometry = new Three.BufferGeometry();
            bufferGeometry.boundingSphere = new Three.Sphere();
            bufferGeometry.boundingSphere.set(new Three.Vector3(), Infinity);
            bufferGeometry.boundingBox = new Three.Box3();
            bufferGeometry.boundingBox.set(new Three.Vector3(-Infinity, -Infinity, -Infinity), new Three.Vector3(+Infinity, +Infinity, +Infinity));
            const material = useNormals ? this.litMaterial : this.unlitMaterial;
            material.map = this._setVertexAttributes(bufferGeometry);
            const mesh = new Three.Mesh(bufferGeometry, material);
            mesh.scale.x = 0.001;
            mesh.scale.y = 0.001;
            mesh.scale.z = 0.001;
            this.mesh = mesh;
            this.bufferGeometry = bufferGeometry;
        }
    }
    getMesh() {
        return this.mesh;
    }
    _setVertexAttributes(bufferGeometry) {
        const { gl } = this;
        const { posBuf } = this.clientBuffers;
        const revision = Number(Three.REVISION);
        const posAttr = revision >= 120
            ? new Three.GLBufferAttribute(posBuf, gl.FLOAT, 3, 0)
            : new Three.GLBufferAttribute(gl, posBuf, gl.FLOAT, 3, 0);
        bufferGeometry.setAttribute('position', posAttr);
        if (this.clientBuffers.norBuf) {
            const norAttr = revision >= 120
                ? new Three.GLBufferAttribute(this.clientBuffers.norBuf, gl.FLOAT, 3, 0)
                : new Three.GLBufferAttribute(gl, this.clientBuffers.norBuf, gl.FLOAT, 3, 0);
            bufferGeometry.setAttribute('normal', norAttr);
        }
        const { uvBuf } = this.clientBuffers;
        const uvAttr = revision >= 120
            ? new Three.GLBufferAttribute(uvBuf, gl.UNSIGNED_SHORT, 2, 0)
            : new Three.GLBufferAttribute(gl, uvBuf, gl.UNSIGNED_SHORT, 2, 0);
        uvAttr.normalized = true;
        bufferGeometry.setAttribute('uv', uvAttr);
        const { indexBuf } = this.clientBuffers;
        const indAttr = revision >= 120
            ? new Three.GLBufferAttribute(indexBuf, this._fileInfo.indicesComponentType, 0, 0)
            : new Three.GLBufferAttribute(gl, indexBuf, this._fileInfo.indicesComponentType, 0, 0);
        bufferGeometry.setIndex(indAttr);
        const texture = new Three.Texture();
        const texProps = this.renderer.properties.get(texture);
        texProps.__webglTexture = this.clientBuffers.tex;
        var saveTex = gl.getParameter(gl.TEXTURE_BINDING_2D);
        gl.bindTexture(gl.TEXTURE_2D, texProps.__webglTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this._fileInfo.videoWidth, this._fileInfo.videoHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.bindTexture(gl.TEXTURE_2D, saveTex);
        this.hvo.setBuffers(this.clientBuffers);
        return texture;
    }
    onUpdate(updated, currentFrameInfo) {
        if (!updated) {
            return;
        }
        const min = currentFrameInfo.bboxMin;
        const max = currentFrameInfo.bboxMax;
        const bufferGeometry = this.bufferGeometry;
        bufferGeometry.boundingBox.min.x = min[0];
        bufferGeometry.boundingBox.min.y = min[1];
        bufferGeometry.boundingBox.min.z = min[2];
        bufferGeometry.boundingBox.max.x = max[0];
        bufferGeometry.boundingBox.max.y = max[1];
        bufferGeometry.boundingBox.max.z = max[2];
        bufferGeometry.boundingBox.getCenter(bufferGeometry.boundingSphere.center);
        const maxSide = Math.max(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
        bufferGeometry.boundingSphere.radius = maxSide * 0.5;
        bufferGeometry.index.count = currentFrameInfo.primCount;
    }
    onClose() {
        if (this.bufferGeometry) {
            this.bufferGeometry.index.count = 0;
        }
    }
}
export { States };
//# sourceMappingURL=holovideoobject-three.js.map